import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import notificacionService from '../services/notificacionService';
import eventoService from '../services/eventoService';

// Notificaciones generales del sistema (excluye ANSES y WhatsApp desconexión/reconexión,
// que tienen su propio aviso — ver useNotificacionesAnses y WhatsAppDesconexionAlert).
// Extraído de components/NotificacionCampana.js para que la campana desktop y la campana
// unificada mobile lean la misma fuente sin duplicar el poller de 60s. Expone
// window.recargarCampanaNotificaciones, que otras pantallas llaman para forzar un refresco
// inmediato (ver FormularioIniciarTramiteRTI.js, FormularioIniciarTramiteART.js,
// CalendarioPage.js, CalendarioLateral.js) — debe seguir asignándose sin importar si está
// montada la campana desktop o la mobile.
export default function useNotificacionesSistema() {
    const [notificaciones, setNotificaciones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const navigate = useNavigate();

    const cargarNotificaciones = useCallback(async () => {
        try {
            // 1. Consultar tanto pendientes como las últimas enviadas en paralelo
            const [pendientesRes, ultimasRes] = await Promise.all([
                notificacionService.getPendientes(),
                notificacionService.getUltimas()
            ]);

            const rawPendientes = pendientesRes?.data ?? pendientesRes;
            const rawUltimas = ultimasRes?.data ?? ultimasRes;
            const pendientes = Array.isArray(rawPendientes) ? rawPendientes : [];
            const ultimas = Array.isArray(rawUltimas) ? rawUltimas : [];

            if (!Array.isArray(rawPendientes)) console.warn('⚠️ Campana: pendientes no es array:', rawPendientes);
            if (!Array.isArray(rawUltimas)) console.warn('⚠️ Campana: ultimas no es array:', rawUltimas);

            // 2. Combinar y eliminar duplicados, dando prioridad a la info de 'pendientes'
            const notificacionesMap = new Map();

            // Primero agregar las pendientes (no leídas)
            pendientes.forEach(n => notificacionesMap.set(n.id, n));

            // Luego agregar las últimas leídas, solo si no existen ya
            ultimas.forEach(n => {
                if (!notificacionesMap.has(n.id)) {
                    notificacionesMap.set(n.id, n);
                }
            });

            // 3. Convertir el mapa a un array y sacar las que tienen su propio aviso dedicado:
            // ANSES (campana propia, ver useNotificacionesAnses) y WhatsApp desconectado/
            // reconectado (modal grande de WhatsAppDesconexionAlert) — mostrarlas también acá
            // sería duplicar el mismo aviso en dos lugares. Siguen existiendo en /notificaciones
            // (getHistorial), esto solo las saca de esta campana. Ordenar por fecha descendente.
            const combinedNotificaciones = Array.from(notificacionesMap.values())
                .filter(n => n.origen !== 'ANSES' && n.origen !== 'WHATSAPP_DESCONEXION' && n.origen !== 'WHATSAPP_RECONEXION')
                .sort((a, b) => new Date(b.fechaProgramada) - new Date(a.fechaProgramada));

            setNotificaciones(combinedNotificaciones);
        } catch (err) {
            console.error('Error al obtener notificaciones:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    const marcarLeida = useCallback((id) => {
        setNotificaciones(prev => prev.map(n => n.id === id ? { ...n, leido: true } : n));
        notificacionService.marcarComoLeidaUsuario(id).catch(err =>
            console.error('Error al marcar notificación como leída:', err)
        );
    }, []);

    // { onNavegar } se llama justo antes de navegar a un trámite — cada consumidor lo usa
    // para cerrar su propio overlay (popover desktop, sheet mobile). Si la notificación no
    // tiene trámite pero sí evento, se abre el modal de detalle y el overlay NO se cierra
    // solo (mismo comportamiento que tenía NotificacionCampana.js hasta ahora).
    const abrirNotificacion = useCallback(async (notif, { onNavegar } = {}) => {
        if (!notif.id) return;

        // Marcar como leída al tocar — dimea visualmente y resta del contador
        if (!notif.leido) marcarLeida(notif.id);

        // Si la notificación tiene tramite asociado, navegar directo sin fetch extra
        if (notif.idTramite) {
            navigate(`/tramites/${notif.idTramite}`);
            onNavegar?.();
            return;
        }

        // Sin tramite: intentar buscar datos del evento para el modal
        if (notif.idEvento) {
            try {
                const resEvento = await eventoService.getEventoById(notif.idEvento);
                const evento = resEvento?.data;
                if (evento) setSelectedEvent(evento);
            } catch (err) {
                console.error('Error al obtener evento:', err);
            }
        }
    }, [navigate, marcarLeida]);

    const descartar = useCallback(async (idNotificacion) => {
        if (!idNotificacion) return;
        try {
            await notificacionService.marcarComoLeidaUsuario(idNotificacion);
            await cargarNotificaciones();
        } catch (err) {
            console.error('Error al descartar notificación:', err);
        }
    }, [cargarNotificaciones]);

    const closeEventModal = useCallback(() => setSelectedEvent(null), []);

    useEffect(() => {
        cargarNotificaciones();
        const intervalo = setInterval(cargarNotificaciones, 60000); // Recargar cada minuto

        // Permite refrescar campana inmediatamente desde otras pantallas (ej: edición de evento).
        window.recargarCampanaNotificaciones = cargarNotificaciones;

        return () => {
            clearInterval(intervalo);
            delete window.recargarCampanaNotificaciones;
        };
    }, [cargarNotificaciones]);

    const nuevasCount = notificaciones.filter(n => n?.leido === false).length;

    return {
        notificaciones,
        loading,
        nuevasCount,
        hayNoLeidas: nuevasCount > 0,
        selectedEvent,
        closeEventModal,
        marcarLeida,
        abrirNotificacion,
        descartar,
        recargar: cargarNotificaciones,
    };
}
